import * as React from 'react';
import RcSlider, { SliderProps } from 'rc-slider';
import { MarkObj } from 'rc-slider/lib/Marks';
import classNames from 'classnames';

import { useTheme } from '../../../contexts/theme';
import Text from '../Text';

import 'rc-slider/assets/index.css';
import styles from './Slider.module.scss';

export interface ColorProp {
  key: number;
  color: string;
}

export interface Props extends Omit<SliderProps, 'onChange'> {
  colorMap?: ColorProp[];
  min?: number;
  max?: number;
  marks: Record<string | number, React.ReactNode | MarkObj>;
  value: number;
  onChange: (n: number) => void;
}

/**
 * Renders the Handle component
 * @param percentage
 * @param accentColor
 * @returns
 */
const renderHandle =
  (percentage: number, accentColor: string): SliderProps['handleRender'] =>
  (node, { prefixCls, value, ...handleProps }) =>
    (
      <div>
        <div
          className={prefixCls}
          style={{
            left: `calc(${percentage}% - 18px)`,
            top: -52,
          }}
          role="spinbutton"
          aria-valuenow={percentage}
          {...handleProps}
        >
          <div
            className={styles.bubble}
            style={{ backgroundColor: accentColor }}
          >
            <Text className="w-100" as="span" color="white" weight="bold">
              {value}
            </Text>
          </div>
          <div
            className={styles.polygon}
            style={{ backgroundColor: accentColor }}
          />
        </div>
        <div style={{ marginTop: '-14px' }}>{node}</div>
      </div>
    );

export const Slider: React.FunctionComponent<Props> = ({
  colorMap,
  min = 0,
  max = 10,
  marks,
  value,
  onChange,
  ...sliderProps
}) => {
  const { theme } = useTheme();

  const colorFromMap = colorMap?.find((c) => c.key === value)?.color;
  const accentColor = colorFromMap || theme.color.primary;

  const change = (value || 0) - min;
  const percentage = (change / (max - min)) * 100;

  return (
    <div className={classNames(styles.wrapper)}>
      <RcSlider
        min={min}
        max={max}
        defaultValue={min}
        marks={marks}
        value={value}
        onChange={(v) => {
          if (typeof v === 'number') onChange(v);
        }}
        trackStyle={{
          backgroundColor: accentColor,
          transition: 'background-color 0.2s',
        }}
        handleStyle={{
          backgroundColor: accentColor,
          transition: 'background-color 0.2s',
        }}
        handleRender={renderHandle(percentage, accentColor)}
        {...sliderProps}
      />
    </div>
  );
};

export default Slider;
